import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowLeft, ArrowRight, BookOpen, ShieldAlert, Users, Compass, Feather,
  HandHeart, Church, Home as HomeIcon, ExternalLink, Search, Flag, Heart, Award,
} from 'lucide-react';

/* ───────────────────────────────────────────────────────────────────────────
 * Shared vetted-organizations Resources page.
 *
 * Two audiences → two datasets:
 *   • MEMBER_CATEGORIES     — first-person "for the man doing the work"
 *   • SUPPORTER_CATEGORIES  — for the people supporting Black & Brown men
 *
 * Both datasets share the SAME 10 categories and (mostly) the SAME orgs, with
 * blurbs and one category ("Support for the Supporter" ↔ "Community &
 * Brotherhood") swapped for perspective.
 * ────────────────────────────────────────────────────────────────────────── */

const CRISIS_ORGS = [
  {
    name: '988 Suicide & Crisis Lifeline',
    url: 'https://988lifeline.org',
    blurb: 'Call or text 988. Free, confidential, 24/7. English & Spanish. The single most important number to memorize.',
    tag: 'National · 24/7',
  },
  {
    name: 'Crisis Text Line',
    url: 'https://www.crisistextline.org',
    blurb: 'Text HOME to 741741 to reach a trained crisis counselor. Silent, safe, and text-first — often more accessible when talking feels impossible.',
    tag: 'Text-based · 24/7',
  },
  {
    name: 'Veterans Crisis Line',
    url: 'https://www.veteranscrisisline.net',
    blurb: 'Dial 988, then press 1. Or text 838255. Staffed by responders trained in military and veteran experience.',
    tag: 'Veterans · 24/7',
  },
  {
    name: 'SAMHSA National Helpline',
    url: 'https://www.samhsa.gov/find-help/national-helpline',
    blurb: '1-800-662-HELP (4357). Free treatment referral and information for mental health and substance use disorders.',
    tag: 'National · 24/7',
  },
];

const CRISIS_SUPPORTER_TAIL = {
  name: 'National Domestic Violence Hotline',
  url: 'https://www.thehotline.org',
  blurb: '1-800-799-7233 or text START to 88788. If the person you\u2019re supporting is causing harm \u2014 or if you feel unsafe \u2014 start here.',
  tag: 'National · 24/7',
};
const CRISIS_MEMBER_TAIL = {
  name: 'National Domestic Violence Hotline',
  url: 'https://www.thehotline.org',
  blurb: '1-800-799-7233 or text START to 88788. If you\u2019re being hurt \u2014 or if you\u2019re afraid you might hurt someone \u2014 you can reach out. Free, confidential.',
  tag: 'National · 24/7',
};

const BLACK_MENS_MH_ORGS_MEMBER = [
  {
    name: 'BEAM \u2014 Black Emotional and Mental Health Collective',
    url: 'https://beam.community',
    blurb: 'Healing circles, peer-support training, and a national directory of Black mental-health providers you can actually see yourself in.',
    tag: 'National',
  },
  {
    name: 'Boris Lawrence Henson Foundation',
    url: 'https://borislhensonfoundation.org',
    blurb: 'Free therapy sessions for Black individuals and families through the Free Virtual Therapy Support program. Register once, come back as needed.',
    tag: 'Free therapy',
  },
  {
    name: 'The Confess Project of America',
    url: 'https://theconfessprojectofamerica.org',
    blurb: 'Trains barbers to hold real mental-health conversations. If therapy still feels far away, your barber might be the first stop.',
    tag: 'Community-based',
  },
  {
    name: 'Black Men Heal',
    url: 'https://blackmenheal.org',
    blurb: 'Up to 8 free therapy sessions for Black men, plus peer-led community groups. Built by Black men, for Black men.',
    tag: 'Free services',
  },
  {
    name: 'Melanin & Mental Health',
    url: 'https://www.melaninandmentalhealth.com',
    blurb: 'Directory of Black and Latinx therapists. The "Between Sessions" podcast is a free, ongoing companion between visits.',
    tag: 'Directory · Podcast',
  },
];

const BLACK_MENS_MH_ORGS_SUPPORTER = [
  { ...BLACK_MENS_MH_ORGS_MEMBER[0], blurb: 'Training programs, healing circles, and a national directory of Black mental health providers.' },
  { ...BLACK_MENS_MH_ORGS_MEMBER[1] },
  { ...BLACK_MENS_MH_ORGS_MEMBER[2], blurb: 'Trains barbers as mental health advocates. If he trusts his barber before he trusts a therapist \u2014 this is your bridge.' },
  { ...BLACK_MENS_MH_ORGS_MEMBER[3], blurb: 'Free mental health services (up to 8 sessions) for Black men, plus community support groups.' },
  { ...BLACK_MENS_MH_ORGS_MEMBER[4], blurb: 'Directory of Black and Latinx therapists, plus the "Between Sessions" podcast for ongoing learning.' },
];

const DEPRESSION_ORGS_MEMBER = [
  { name: 'HeadsUpGuys', url: 'https://headsupguys.org',
    blurb: 'Practical strategies for managing and preventing depression. Written for men — no jargon, no lectures. The self-check tool is a solid five-minute investment.',
    tag: 'Men-focused' },
  { name: 'Man Therapy', url: 'https://mantherapy.org',
    blurb: 'Uses humor to break the stigma. The "18-Point Head Inspection" is a screening tool that lands when clinical language doesn\u2019t.',
    tag: 'Screening tool' },
  { name: 'Movember \u2014 Mental Health', url: 'https://us.movember.com/en/mental-health',
    blurb: 'International movement for men\u2019s mental health. Practical tools plus community, so this doesn\u2019t stay a solo problem.',
    tag: 'Community · Tools' },
  { name: 'Anxiety & Depression Association of America', url: 'https://adaa.org',
    blurb: 'Evidence-based fact sheets and a therapist directory. Their peer-to-peer online community is quiet, moderated, and real.',
    tag: 'National · Directory' },
];
const DEPRESSION_ORGS_SUPPORTER = [
  { ...DEPRESSION_ORGS_MEMBER[0], blurb: 'Practical strategies for managing and preventing depression in men. Excellent self-check tool you can send him.' },
  { ...DEPRESSION_ORGS_MEMBER[1], blurb: 'Uses humor to break the stigma. Includes an "18-Point Head Inspection" screening tool that lands with men who avoid clinical language.' },
  { ...DEPRESSION_ORGS_MEMBER[2], blurb: 'International movement for men\u2019s mental health. "Movember Conversations" role-play tool teaches you how to check on the men in your life.', tag: 'Conversation tool' },
  { ...DEPRESSION_ORGS_MEMBER[3], blurb: 'Fact sheets, therapist directory, and peer-to-peer online communities. Solid clinical grounding.' },
];

const THERAPY_DIRECTORIES = [
  { name: 'Psychology Today Therapist Finder', url: 'https://www.psychologytoday.com/us/therapists',
    blurb: 'The largest and most-updated US directory. Filter by insurance, specialty, sliding scale, teletherapy, race, gender, and religion.',
    tag: 'Largest directory' },
  { name: 'Open Path Collective', url: 'https://openpathcollective.org',
    blurb: 'Sliding-scale therapy from $40\u2013$80/session for people without insurance. Nationwide, in-person and online.',
    tag: 'Sliding scale' },
  { name: 'Inclusive Therapists', url: 'https://www.inclusivetherapists.com',
    blurb: 'BIPOC, LGBTQ+, disability-affirming, and neurodiversity-affirming therapist directory. Deep filters for identity match.',
    tag: 'Identity-affirming' },
  { name: 'National Queer & Trans Therapists of Color Network', url: 'https://nqttcn.com',
    blurb: 'Therapists of color who are queer or trans-affirming. Also offers a mental-health fund.',
    tag: 'BIPOC · LGBTQ+' },
];
const THERAPY_DIRECTORIES_SUPPORTER = [
  ...THERAPY_DIRECTORIES.slice(0, 3),
  { name: 'Therapy for Black Girls', url: 'https://therapyforblackgirls.com',
    blurb: 'Directory of Black women therapists. If you\u2019re the woman supporting him, this is for you.',
    tag: 'For supporters' },
  THERAPY_DIRECTORIES[3],
];
const THERAPY_DIRECTORIES_MEMBER = THERAPY_DIRECTORIES;

const SUBSTANCE_ORGS_MEMBER = [
  { name: 'SAMHSA Treatment Locator', url: 'https://findtreatment.gov',
    blurb: 'Federal, confidential locator for licensed substance-use treatment providers near you. Free, no insurance required to search.',
    tag: 'National' },
  { name: 'Alcoholics Anonymous', url: 'https://www.aa.org',
    blurb: 'Free, worldwide peer fellowship. Find in-person and online meetings 24/7. No membership fees. No screening. Just show up.',
    tag: 'Free · Peer' },
  { name: 'Narcotics Anonymous', url: 'https://www.na.org',
    blurb: 'Same model as AA but for any drug. Meetings in almost every US city. Anonymity is a foundational principle — nothing you say leaves.',
    tag: 'Free · Peer' },
  { name: 'SMART Recovery', url: 'https://www.smartrecovery.org',
    blurb: 'Science-based (CBT + motivational interviewing) alternative to 12-step. Secular, self-empowering, plenty of online meetings.',
    tag: 'Evidence-based' },
];
const SUBSTANCE_ORGS_SUPPORTER = [
  SUBSTANCE_ORGS_MEMBER[0],
  { name: 'Al-Anon Family Groups', url: 'https://al-anon.org',
    blurb: 'Peer support meetings for people whose lives are affected by someone else\u2019s drinking. In-person, phone, and online.',
    tag: 'Free · Peer' },
  { name: 'Nar-Anon', url: 'https://www.nar-anon.org',
    blurb: 'Same model as Al-Anon, focused on families affected by addiction to any substance.',
    tag: 'Free · Peer' },
  { name: 'SMART Recovery Family & Friends', url: 'https://www.smartrecovery.org',
    blurb: 'Science-based alternative to 12-step \u2014 CRAFT-informed. Meetings and free handbook for families.',
    tag: 'Evidence-based' },
];

const GRIEF_ORGS = [
  { name: "What's Your Grief", url: 'https://whatsyourgrief.com',
    blurb: 'Evidence-informed, non-judgmental writing on every dimension of grief. Free articles, courses, and downloadable worksheets.',
    tag: 'Free · Deep' },
  { name: 'GriefShare', url: 'https://www.griefshare.org',
    blurb: 'Faith-based grief recovery support groups in over 20,000 locations worldwide.',
    tag: 'Faith-based' },
  { name: 'Modern Loss', url: 'https://modernloss.com',
    blurb: 'Candid conversation about grief \u2014 written for adults who lost people in ways that don\u2019t fit traditional mourning.',
    tag: 'Community' },
];
const GRIEF_ORGS_MEMBER = [
  ...GRIEF_ORGS,
  { name: 'The Dinner Party', url: 'https://www.thedinnerparty.org',
    blurb: 'Peer-led community for people in their 20s\u201340s who\u2019ve experienced significant loss. Small dinner groups, free to join.',
    tag: 'Peer · 20s\u201340s' },
];
const GRIEF_ORGS_SUPPORTER = [
  ...GRIEF_ORGS,
  { name: 'Dougy Center \u2014 Guides for Adults Supporting Grievers', url: 'https://www.dougy.org/grief-support-resources',
    blurb: 'Practical downloadable guides for supporting a grieving partner, teen, or friend. Written by grief specialists.',
    tag: 'Guides' },
];

const VETERANS_ORGS = [
  { name: 'Veterans Crisis Line', url: 'https://www.veteranscrisisline.net',
    blurb: 'Dial 988 then press 1, text 838255, or chat online. Confidential, 24/7. For veterans, service members, and their loved ones.',
    tag: 'Crisis · 24/7' },
  { name: 'Give an Hour', url: 'https://giveanhour.org',
    blurb: 'Free mental-health services to veterans, service members, and their families through a nationwide network of licensed volunteers.',
    tag: 'Free therapy' },
  { name: 'Team Red, White & Blue', url: 'https://teamrwb.org',
    blurb: 'Community that connects veterans through physical, mental, and social activities. Local chapters in most cities.',
    tag: 'Community' },
  { name: 'Cohen Veterans Network', url: 'https://www.cohenveteransnetwork.org',
    blurb: 'Clinics offering low-cost, high-quality mental-health care to post-9/11 veterans and their families \u2014 whether or not they used the VA.',
    tag: 'Low-cost care' },
];

const FATHERHOOD_ORGS_MEMBER = [
  { name: 'National Fatherhood Initiative', url: 'https://www.fatherhood.org',
    blurb: 'Research-backed programs, guides, and toolkits for engaged fatherhood. Especially strong on reentry and incarcerated fathers.',
    tag: 'Programs' },
  { name: 'The Gottman Institute', url: 'https://www.gottman.com',
    blurb: 'Decades of couples research turned into practical tools. The free blog is a goldmine when you\u2019re trying to repair a relationship.',
    tag: 'Couples · Free blog' },
  { name: 'Center for Nonviolent Communication', url: 'https://www.cnvc.org',
    blurb: 'The NVC framework: how to say hard things without escalating. Especially useful when you feel yourself shutting down.',
    tag: 'Communication' },
  { name: 'Zero to Three', url: 'https://www.zerotothree.org',
    blurb: 'Free, science-based tools for parents of babies and toddlers. If you\u2019re a new dad, start here.',
    tag: 'New parents' },
];
const FATHERHOOD_ORGS_SUPPORTER = [
  { ...FATHERHOOD_ORGS_MEMBER[0] },
  { ...FATHERHOOD_ORGS_MEMBER[1], blurb: 'Decades of couples research turned into practical tools. Their free blog is a goldmine for anyone trying to repair a relationship.' },
  { ...FATHERHOOD_ORGS_MEMBER[2], blurb: 'NVC framework: how to say hard things without escalating. Especially useful when he shuts down.' },
  { ...FATHERHOOD_ORGS_MEMBER[3], blurb: 'Free, science-based tools for parents of babies and toddlers. Perfect for new dads (and the people supporting them).' },
];

const FAITH_ORGS = [
  { name: 'HOPE Coalition America', url: 'https://operationhope.org',
    blurb: 'Interfaith network connecting congregations to mental-health resources. Free trainings for faith leaders.',
    tag: 'Interfaith' },
  { name: 'Muslim Wellness Foundation', url: 'https://muslimwellness.com',
    blurb: 'Culturally grounded mental-health resources, training for imams, and a directory of Muslim mental-health providers.',
    tag: 'Muslim community' },
  { name: 'GriefShare (Faith-based)', url: 'https://www.griefshare.org',
    blurb: '13-week grief recovery groups rooted in Christian faith. Held in 20,000+ churches worldwide.',
    tag: 'Christian grief' },
];

// Member-only category (replaces "Support for the Supporter")
const BROTHERHOOD_COMMUNITY_ORGS = [
  { name: '100 Black Men of America', url: 'https://100blackmen.org',
    blurb: 'National mentoring, education, and economic-empowerment organization. Chapters in most major US cities.',
    tag: 'Mentorship' },
  { name: 'My Brother\u2019s Keeper Alliance (Obama Foundation)', url: 'https://www.obama.org/our-work/mbk-alliance',
    blurb: 'National coalition focused on closing opportunity gaps for boys and young men of color. Programs, cohorts, and community events.',
    tag: 'National coalition' },
  { name: 'National CARES Mentoring Movement', url: 'https://www.caresmentoring.org',
    blurb: 'Recruits and trains Black adults to mentor Black youth. If mentoring one younger brother is on your list \u2014 start here.',
    tag: 'Mentoring · Black' },
  { name: 'Big Brothers Big Sisters of America', url: 'https://www.bbbs.org',
    blurb: 'The oldest, largest, and most-vetted 1:1 mentoring network. Become a Big \u2014 or ask to be matched with one.',
    tag: 'Mentorship' },
  { name: 'Barbershop Books', url: 'https://barbershopbooks.org',
    blurb: 'Puts child-selected reading libraries inside Black-owned barbershops. Sustainable local involvement, minimal time investment.',
    tag: 'Community · Local' },
];

// Supporter-only category (replaces "Community & Brotherhood")
const CAREGIVER_ORGS = [
  { name: 'NAMI Family Support Group', url: 'https://www.nami.org/support-education/support-groups/nami-family-support-group',
    blurb: 'Free peer-led support groups for family members supporting a loved one with mental-health challenges. Local chapters nationwide.',
    tag: 'National · Free' },
  { name: 'NAMI Family-to-Family', url: 'https://www.nami.org/support-education/mental-health-education/nami-family-to-family',
    blurb: 'Evidence-based 8-session education program for family and friends. Free. Taught by other family members who\u2019ve been there.',
    tag: 'Education · Free' },
  { name: 'Mental Health America \u2014 Family & Caregivers', url: 'https://mhanational.org',
    blurb: 'Practical guides for caregivers, screening tools you can share, and a state-by-state resource finder.',
    tag: 'Guides · Tools' },
  { name: 'Alliance of Hope', url: 'https://allianceofhope.org',
    blurb: 'For people who have lost someone to suicide \u2014 online community, memorial, healing resources. Founded by survivors.',
    tag: 'Peer community' },
  { name: 'Caregiver Action Network', url: 'https://www.caregiveraction.org',
    blurb: 'Family caregiver toolbox, peer forums, and a help desk staffed by people who live it.',
    tag: 'National' },
];

/* ─── MEMBER dataset ─── */
const MEMBER_CATEGORIES = [
  { id: 'crisis', icon: ShieldAlert, color: 'red',
    title: 'Crisis & Immediate Safety',
    desc: 'If you\u2019re in danger \u2014 or thinking about hurting yourself \u2014 these lines are for you. 24/7, free, confidential.',
    orgs: [...CRISIS_ORGS, CRISIS_MEMBER_TAIL],
  },
  { id: 'black-mens-mental-health', icon: HandHeart, color: 'gold',
    title: 'Black Men\u2019s Mental Health',
    desc: 'Built for you. Providers, programs, and communities where you don\u2019t have to explain your whole self before you get help.',
    orgs: BLACK_MENS_MH_ORGS_MEMBER,
  },
  { id: 'brotherhood-community', icon: Award, color: 'navy',
    title: 'Brotherhood & Community',
    desc: 'Places to get mentored, mentor others, and belong. The right room changes everything.',
    orgs: BROTHERHOOD_COMMUNITY_ORGS,
  },
  { id: 'depression-anxiety', icon: Heart, color: 'gold',
    title: 'Depression & Anxiety',
    desc: 'Tools built for men. Practical, direct, no shame. Start with any one \u2014 they\u2019re all free.',
    orgs: DEPRESSION_ORGS_MEMBER,
  },
  { id: 'therapy-directories', icon: Search, color: 'navy',
    title: 'Find a Therapist',
    desc: 'Databases you can search yourself \u2014 by state, insurance, sliding scale, identity, and specialty.',
    orgs: THERAPY_DIRECTORIES_MEMBER,
  },
  { id: 'substance-recovery', icon: Compass, color: 'gold',
    title: 'Substance Use & Recovery',
    desc: 'If drinking, weed, pills, or something else has crossed a line you didn\u2019t mean to cross \u2014 there is no shame here. Only options.',
    orgs: SUBSTANCE_ORGS_MEMBER,
  },
  { id: 'grief-loss', icon: Feather, color: 'navy',
    title: 'Grief & Loss',
    desc: 'For navigating the death of someone you loved, a breakup, or a major loss you\u2019re still figuring out how to name.',
    orgs: GRIEF_ORGS_MEMBER,
  },
  { id: 'veterans', icon: Flag, color: 'gold',
    title: 'Veterans & Military',
    desc: 'For the men who served. Reintegration, PTSD, and everything after \u2014 with people who\u2019ve been where you\u2019ve been.',
    orgs: VETERANS_ORGS,
  },
  { id: 'fatherhood-relationship', icon: HomeIcon, color: 'navy',
    title: 'Fatherhood & Relationships',
    desc: 'For the long game. Kids. Partners. The people who need the best version of you.',
    orgs: FATHERHOOD_ORGS_MEMBER,
  },
  { id: 'faith-community', icon: Church, color: 'gold',
    title: 'Faith-Based Support',
    desc: 'When church, mosque, or spiritual community is part of how you heal.',
    orgs: FAITH_ORGS,
  },
];

/* ─── SUPPORTER dataset ─── */
const SUPPORTER_CATEGORIES = [
  { id: 'crisis', icon: ShieldAlert, color: 'red',
    title: 'Crisis & Immediate Safety',
    desc: 'For the moments that can\u2019t wait. Call, text, or chat 24/7.',
    orgs: [...CRISIS_ORGS, CRISIS_SUPPORTER_TAIL],
  },
  { id: 'black-mens-mental-health', icon: HandHeart, color: 'gold',
    title: 'Black Men\u2019s Mental Health',
    desc: 'Organizations built specifically for Black men and by Black clinicians. Culturally responsive, trauma-informed.',
    orgs: BLACK_MENS_MH_ORGS_SUPPORTER,
  },
  { id: 'caregiver-support', icon: Users, color: 'navy',
    title: 'Support for the Supporter',
    desc: 'You cannot pour from an empty cup. Groups, tools, and peer communities for people who care for others.',
    orgs: CAREGIVER_ORGS,
  },
  { id: 'depression-anxiety', icon: Heart, color: 'gold',
    title: 'Depression & Anxiety in Men',
    desc: 'Educational resources specifically framed for men \u2014 no shame, no jargon, real language.',
    orgs: DEPRESSION_ORGS_SUPPORTER,
  },
  { id: 'therapy-directories', icon: Search, color: 'navy',
    title: 'Therapy Directories',
    desc: 'Databases you can search on his behalf \u2014 by state, insurance, specialty, sliding scale, and identity.',
    orgs: THERAPY_DIRECTORIES_SUPPORTER,
  },
  { id: 'substance-recovery', icon: Compass, color: 'gold',
    title: 'Substance Use & Recovery',
    desc: 'When drinking, weed, pills, or other substances have become part of the picture \u2014 for him or for you.',
    orgs: SUBSTANCE_ORGS_SUPPORTER,
  },
  { id: 'grief-loss', icon: Feather, color: 'navy',
    title: 'Grief & Loss',
    desc: 'For men navigating a death, breakup, or major loss \u2014 and for the people helping them through it.',
    orgs: GRIEF_ORGS_SUPPORTER,
  },
  { id: 'veterans', icon: Flag, color: 'gold',
    title: 'Veterans & Military Families',
    desc: 'For the men who served \u2014 and the families supporting them through reintegration, PTSD, and everything after.',
    orgs: VETERANS_ORGS,
  },
  { id: 'fatherhood-relationship', icon: HomeIcon, color: 'navy',
    title: 'Fatherhood & Relationships',
    desc: 'For men and the partners, parents, and children in their lives \u2014 skills for the long game.',
    orgs: FATHERHOOD_ORGS_SUPPORTER,
  },
  { id: 'faith-community', icon: Church, color: 'gold',
    title: 'Faith-Based Support',
    desc: 'When church, mosque, or spiritual community is part of the healing path.',
    orgs: FAITH_ORGS,
  },
];

/* ─── Hero copy per audience ─── */
const MEMBER_HERO = {
  kicker: 'Curated resources',
  h1a: 'Trusted organizations',
  h1b: 'built for the man doing the work.',
  body:
    'A hand-picked library of {n}+ vetted organizations \u2014 crisis lines, culturally responsive therapists, ' +
    'peer communities, mentorship networks, and specialized help for what you\u2019re carrying. Every link is real, active, and vetted.',
  searchPh: 'Search — try “therapy,” “Black men,” “addiction,” “veterans,” “grief”…',
  suggestPath: '/app',
  backHref: '/app/profile',
  backLabel: 'Back to profile',
  panelTestid: 'member-resources-page',
  footerNote: 'Brotherhood+ does not endorse or profit from any listed organization. Educational content only. ' +
    'In immediate danger, call 911. Otherwise, call or text 988 — free, confidential, 24/7.',
};

const SUPPORTER_HERO = {
  kicker: 'Curated resources',
  h1a: 'Trusted organizations',
  h1b: 'for the people supporting men.',
  body:
    'A curated library of {n}+ vetted organizations \u2014 crisis lines, culturally responsive therapists, ' +
    'caregiver support groups, and specialized help for the men in your life. Every link is real, active, and vetted.',
  searchPh: 'Search — try “crisis,” “Black men,” “veterans,” “grief,” “addiction”…',
  suggestPath: '/app/support-home',
  backHref: '/app/support-home',
  backLabel: 'Back to Support Home',
  panelTestid: 'supporter-resources-page',
  footerNote: 'Brotherhood Support does not endorse or profit from any listed organization. Educational content only. ' +
    'In immediate danger, call 911. Otherwise, call or text 988 — free, confidential, 24/7.',
};

const COLOR_MAP = {
  red:   { icon: 'text-red-600',  chip: 'bg-red-50 text-red-700', bg: 'rgba(220,38,38,0.10)' },
  gold:  { icon: 'text-gold',     chip: 'bg-gold/10 text-navy',   bg: 'rgba(197,160,89,0.14)' },
  navy:  { icon: 'text-navy',     chip: 'bg-navy/5 text-navy',    bg: 'rgba(11,25,44,0.05)' },
};

/* ─── Shared view ─── */
function ResourcesView({ categories, hero }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((cat) => ({
        ...cat,
        orgs: cat.orgs.filter(o =>
          o.name.toLowerCase().includes(q) ||
          o.blurb.toLowerCase().includes(q) ||
          (o.tag || '').toLowerCase().includes(q) ||
          cat.title.toLowerCase().includes(q)
        ),
      }))
      .filter(cat => cat.orgs.length > 0);
  }, [query, categories]);

  const totalOrgs = categories.reduce((a, c) => a + c.orgs.length, 0);

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-8 lg:space-y-10 pb-16" data-testid={hero.panelTestid}>
      <Link
        to={hero.backHref}
        data-testid={`${hero.panelTestid}-back`}
        className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy"
      >
        <ArrowLeft size={14}/> {hero.backLabel}
      </Link>

      {/* HERO */}
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
          <BookOpen size={12} strokeWidth={1.75}/> {hero.kicker}
        </div>
        <h1 className="font-heading text-4xl lg:text-6xl mt-3 lg:mt-4 leading-[1.05] max-w-4xl text-navy">
          {hero.h1a}<br/><span className="italic text-navy/60">{hero.h1b}</span>
        </h1>
        <p className="text-navy/70 mt-4 lg:mt-6 text-base lg:text-lg leading-relaxed max-w-3xl">
          {hero.body.replace('{n}', totalOrgs)}
        </p>
      </div>

      {/* SEARCH */}
      <div className="relative max-w-2xl">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40"/>
        <input
          data-testid={`${hero.panelTestid}-search`}
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={hero.searchPh}
          className="w-full bg-white border border-navy/10 rounded-2xl pl-11 pr-4 py-4 text-sm lg:text-base focus:outline-none focus:ring-2 focus:ring-gold"
        />
      </div>

      {/* CATEGORY ANCHORS */}
      {!query && (
        <nav className="flex flex-wrap gap-2 -mt-2" data-testid={`${hero.panelTestid}-anchors`}>
          {categories.map(cat => (
            <a
              key={cat.id}
              href={`#${cat.id}`}
              data-testid={`resources-anchor-${cat.id}`}
              className="inline-flex items-center gap-1.5 bg-white border border-navy/10 rounded-full px-3.5 py-1.5 text-xs text-navy hover:border-navy/40 transition-smooth"
            >
              <cat.icon size={12} className={COLOR_MAP[cat.color].icon} strokeWidth={1.75}/>
              {cat.title}
            </a>
          ))}
        </nav>
      )}

      {/* Filtered empty state */}
      {filtered.length === 0 && (
        <div className="bg-white rounded-3xl p-10 text-center card-soft">
          <p className="text-navy/60 text-sm">No matches for &ldquo;{query}&rdquo;. Try a different word — every link is still here.</p>
        </div>
      )}

      {/* CATEGORIES */}
      {filtered.map(cat => {
        const color = COLOR_MAP[cat.color];
        return (
          <section
            key={cat.id}
            id={cat.id}
            className="scroll-mt-24"
            data-testid={`resources-category-${cat.id}`}
          >
            <div className="flex items-start gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: color.bg }}>
                <cat.icon size={22} className={color.icon} strokeWidth={1.5}/>
              </div>
              <div>
                <p className="font-heading text-2xl lg:text-3xl text-navy leading-tight">{cat.title}</p>
                <p className="text-sm lg:text-base text-navy/60 mt-1 leading-relaxed max-w-3xl">{cat.desc}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cat.orgs.map((org) => (
                <a
                  key={org.name}
                  href={org.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-org-${cat.id}-${org.name.replace(/[^a-z0-9]/gi, '-').toLowerCase().slice(0, 40)}`}
                  className="group bg-white rounded-2xl p-5 lg:p-6 border border-navy/5 hover:-translate-y-0.5 transition-smooth flex flex-col"
                  style={{ boxShadow: '0 4px 20px rgba(11,25,44,0.04)' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-heading text-lg text-navy leading-snug group-hover:text-gold transition-smooth">{org.name}</p>
                    <ExternalLink size={14} className="text-navy/30 shrink-0 mt-1 group-hover:text-navy transition-smooth"/>
                  </div>
                  <p className="text-sm text-navy/65 mt-2 leading-relaxed flex-1">{org.blurb}</p>
                  {org.tag && (
                    <span className={`inline-flex items-center self-start mt-3 text-[10px] uppercase tracking-widest font-semibold rounded-full px-2.5 py-1 ${color.chip}`}>
                      {org.tag}
                    </span>
                  )}
                </a>
              ))}
            </div>
          </section>
        );
      })}

      {/* FOOTER */}
      <div className="bg-beige rounded-3xl p-6 lg:p-8" data-testid={`${hero.panelTestid}-footer`}>
        <p className="font-heading text-lg text-navy">Something missing?</p>
        <p className="text-sm text-navy/70 mt-2 leading-relaxed max-w-2xl">
          We vet every organization here for legitimacy, longevity, and cultural relevance. If you know a resource
          that&rsquo;s helped you, tell us on the roadmap and we&rsquo;ll consider adding it.
        </p>
        <Link
          to="/app/roadmap"
          data-testid={`${hero.panelTestid}-suggest`}
          className="inline-flex items-center gap-2 mt-4 bg-navy text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-navy-soft transition-smooth"
        >
          Suggest a resource <ArrowRight size={14}/>
        </Link>
      </div>

      <p className="text-center text-xs text-navy/40 leading-relaxed max-w-2xl mx-auto">
        {hero.footerNote}
      </p>
    </div>
  );
}

export default function Resources() {
  const { user } = useAuth();
  const isSupporter = (user?.role || 'member') === 'supporter';
  return isSupporter
    ? <ResourcesView categories={SUPPORTER_CATEGORIES} hero={SUPPORTER_HERO} />
    : <ResourcesView categories={MEMBER_CATEGORIES}   hero={MEMBER_HERO} />;
}
